import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import type { PartnerCompanyProps } from '@services/PartnerCompanyService/dtos';
import { UserRedirectsEnum } from '@services/UserService';

import {
  BaseProfileResourceEnum,
  CompanyStatusEnum,
  PartnerCompaniesContractStatusEnum,
  UserProfileEnum,
} from '@constants';
import { type AccountProps, UserType } from '@contexts';
import { CompanySetupDetailsRouter } from '@flows/private/companySetup/pages/CompanySetupDetails/router';
import { HomeRouter } from '@flows/private/home/router';
import { useAuth, useProfileDetails, useScreenLoading } from '@hooks';
import { BillListRouter } from '@flows/private/financial/pages/List/router';
import { OptionalRoutesEnum } from 'routes/enums';

import { defaultOptionalsSubRoutes } from './constants';
import { useCreateSession } from '../useCreateSession';

export function useSelectPartnerCompany() {
  const { updateAccount, selectedCompany, user } = useAuth();
  const { getUserProfileDetails } = useProfileDetails();
  const navigate = useNavigate();
  const { onScreenLoading } = useScreenLoading();

  const {
    handleAccessResponsibilityLetter,
    handleBeneficiaryServicesExists,
    handleCoparticipationPreviewReport,
    handleCreateSession,
    handleMonthlyFeePreviewReport,
    handleRemoveFromNewCompanies,
    handleSuspendedBeneficiary,
    handleCurrentCompany,
  } = useCreateSession();

  const handleSelectPartnerCompany = useCallback(
    async (selectedPartnerCompany?: PartnerCompanyProps) => {
      if (!selectedPartnerCompany || !selectedCompany) {
        updateAccount({ selectedPartnerCompany: undefined });
        return;
      }

      const { profile } = selectedPartnerCompany;
      const { id = '' } = profile || {};

      onScreenLoading(true);

      const hasSession = await handleCreateSession(
        selectedPartnerCompany?.partnerCompany,
        selectedCompany?.id,
      );

      if (!hasSession) {
        onScreenLoading(false);
        return;
      }

      const currentCompany = await handleCurrentCompany();

      const userProfileDetails = await getUserProfileDetails(id);

      const resources = userProfileDetails?.resources;

      const combinedProfile = profile && {
        ...profile,
        resources,
      };

      const updatedData: AccountProps = {
        selectedPartnerCompany: {
          ...selectedPartnerCompany,
          profile: combinedProfile,
        },
        currentCompany,
        ...defaultOptionalsSubRoutes,
      };

      const hasFinancialResource =
        resources?.[BaseProfileResourceEnum.FINANCIAL];

      if (hasFinancialResource) {
        const monthlyFeePreviewReport = await handleMonthlyFeePreviewReport();

        const coparticipationPreviewReport =
          await handleCoparticipationPreviewReport();

        updatedData.optionalRedirects = {
          ...updatedData?.optionalRedirects,
          [UserRedirectsEnum.MONTHLY_FEE_PREVIEW_REPORT]: Boolean(
            monthlyFeePreviewReport?.previewReport,
          ),
          [UserRedirectsEnum.COPARTICIPATION_PREVIEW_REPORT]: Boolean(
            coparticipationPreviewReport?.coparticipationPreviewReport,
          ),
        };
      }

      const isPartnerCompanyCanceled =
        selectedPartnerCompany?.partnerCompanyStatus ===
        PartnerCompaniesContractStatusEnum.CANCELED;

      const hasBeneficiaryResource =
        resources?.[BaseProfileResourceEnum.BENEFICIARIES];

      if (hasBeneficiaryResource && !isPartnerCompanyCanceled) {
        const suspendedBeneficiaryReport = await handleSuspendedBeneficiary();

        const beneficiaryServicesExists = await handleBeneficiaryServicesExists(
          selectedPartnerCompany?.partnerCompany,
        );

        updatedData.optionalRedirects = {
          ...updatedData?.optionalRedirects,
          [UserRedirectsEnum.SUSPENDED_BENEFICIARY]: Boolean(
            suspendedBeneficiaryReport?.hasSuspendedBeneficiaryReport,
          ),
        };

        updatedData.optionalRoutes = {
          ...updatedData?.optionalRoutes,
          [OptionalRoutesEnum.BENEFICIARY_SERVICES]:
            beneficiaryServicesExists?.isBeneficiaryServicesMenuAccepted,
        };
      }

      const isMaster = combinedProfile?.name === UserProfileEnum.MASTER;
      const { isMainPartnerCompany } = selectedPartnerCompany;

      if (isMaster && isMainPartnerCompany && !isPartnerCompanyCanceled) {
        const accessResponsibilityLetter =
          await handleAccessResponsibilityLetter(
            selectedPartnerCompany?.partnerCompany,
          );

        updatedData.optionalRoutes = {
          ...updatedData?.optionalRoutes,
          [OptionalRoutesEnum.RESPONSIBILITY_LETTER]:
            accessResponsibilityLetter?.isResponsibilityLetterMenuAccepted,
        };
      }

      const backofficeUser = user?.companyType === UserType.Backoffice;
      const isRegisterAccessCompany =
        selectedCompany?.status === CompanyStatusEnum.REGISTER_ACCESS;

      if (selectedCompany?.isNewCompany) {
        await handleRemoveFromNewCompanies(selectedCompany?.id);
        updatedData.selectedCompany = {
          ...selectedCompany,
          isNewCompany: false,
        };
      }

      updateAccount(updatedData);

      onScreenLoading(false);

      const navigationRoutes: Record<string, () => void> = {
        backofficeRegisterAccess: () =>
          navigate(
            CompanySetupDetailsRouter.dynamicPath({
              companyId: selectedCompany?.id,
            }),
          ),
        partnerCompanyCanceled: () => {
          navigate(BillListRouter.path);
        },
        default: () => navigate(HomeRouter.path),
      };

      let navigationKey = 'default';
      if (
        backofficeUser &&
        isRegisterAccessCompany &&
        !isPartnerCompanyCanceled
      ) {
        navigationKey = 'backofficeRegisterAccess';
      } else if (isPartnerCompanyCanceled) {
        navigationKey = 'partnerCompanyCanceled';
      }

      navigationRoutes[navigationKey]();
    },
    [handleRemoveFromNewCompanies, navigate, user, selectedCompany],
  );

  return { handleSelectPartnerCompany };
}
